import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useMarketOverview } from '@/hooks/useMarket';
import { LoadingSpinner } from '@/components/ui';

// ============================================================================
// HOME PAGE - Phase 1: Landing Page Redesign (2026-02-04)
// Goal: 3 clear CTAs, easy on the eyes, immediately understandable
// ============================================================================

export function HomePage() {
  const { language } = useLanguage();
  const { selectedClient, isLoading: clientsLoading } = useClient();
  const t = language === 'es';

  // No client selected - show welcome state
  if (!selectedClient) {
    return <WelcomeState language={language} isLoading={clientsLoading} />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center px-4 pt-16 pb-8">
      {/* Client Context Badge - subtle, top center */}
      <ClientContextBadge
        clientId={selectedClient.client_id}
        clientName={selectedClient.client_name}
        language={language}
      />

      {/* Main Headline */}
      <h1 className="text-3xl md:text-4xl font-bold text-white text-center mt-6 mb-2">
        {t ? '¿Qué te gustaría hacer hoy?' : 'What would you like to do today?'}
      </h1>
      <p className="text-text-secondary text-center mb-10 max-w-lg">
        {t
          ? 'Explora tu mercado, simula escenarios o investiga con personas sintéticas'
          : 'Explore your market, simulate scenarios, or research with synthetic personas'}
      </p>

      {/* 3 CTA Cards - horizontal layout on desktop, vertical on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full">
        <CTACard
          icon="📊"
          title={t ? 'Entender' : 'Understand'}
          subtitle={t ? 'Inteligencia de mercado' : 'Market intelligence'}
          items={t
            ? ['Panorama de mercado', 'Segmentos de consumidor', 'Posición competitiva']
            : ['Market landscape', 'Consumer segments', 'Competitive position']}
          href="/understand"
          cta={t ? 'Explorar' : 'Explore'}
          language={language}
        />
        <CTACard
          icon="🎮"
          title={t ? 'Simular' : 'Simulate'}
          subtitle={t ? 'Ejecutar escenarios' : 'Run scenarios'}
          items={t
            ? ['Optimización de precios', 'Pronóstico de lanzamiento']
            : ['Pricing optimization', 'Launch forecasting']}
          href="/decide"
          cta={t ? 'Iniciar' : 'Start'}
          primary
          language={language}
        />
        <CTACard
          icon="🔬"
          title={t ? 'Investigar' : 'Research'}
          subtitle={t ? 'Consultar y chatear' : 'Query & chat'}
          items={t
            ? ['Hacer preguntas', 'Chatear con personas']
            : ['Ask questions', 'Chat with personas']}
          href="/research"
          cta={t ? 'Preguntar' : 'Ask'}
          language={language}
        />
      </div>

      {/* Recent Activity Bar - subtle footer */}
      <RecentActivityBar
        clientId={selectedClient.client_id}
        language={language}
      />
    </div>
  );
}

// ============================================================================
// CLIENT CONTEXT BADGE - Shows current client with data tier
// ============================================================================

function ClientContextBadge({
  clientId,
  clientName,
  language,
}: {
  clientId: string;
  clientName: string;
  language: string;
}) {
  const t = language === 'es';
  const { data: marketData, isLoading } = useMarketOverview(clientId);

  const dataTier = getDataTier(marketData);
  const marketSize = marketData?.market?.total_value;
  const currency = marketData?.market?.currency || 'USD';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <div className="card px-6 py-4 text-center bg-surface-800/80 border-surface-600">
      {/* Data Tier Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-700/80 text-xs text-text-secondary mb-2">
        <span>{dataTier.icon}</span>
        <span>{dataTier.label} {t ? 'Datos' : 'Tier Data'}</span>
      </div>

      {/* Client Name */}
      <h2 className="text-xl font-semibold text-white">{clientName}</h2>

      {/* Market Size */}
      {marketSize && (
        <p className="text-sm text-text-secondary mt-1">
          {t ? 'Mercado' : 'Market'}: {formatCurrency(marketSize, currency)}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// CTA CARD - Main action card
// ============================================================================

function CTACard({
  icon,
  title,
  subtitle,
  items,
  href,
  cta,
  primary = false,
  language,
}: {
  icon: string;
  title: string;
  subtitle: string;
  items: string[];
  href: string;
  cta: string;
  primary?: boolean;
  language: string;
}) {
  return (
    <Link
      to={href}
      className={`
        card p-6 flex flex-col h-full transition-all
        hover:border-primary-500/40 hover:bg-surface-700/50
        ${primary
          ? 'border-primary-500/50 bg-primary-500/5 ring-1 ring-primary-500/20'
          : 'border-surface-600 bg-surface-800'
        }
      `}
    >
      {/* Icon */}
      <div className={`
        w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4
        ${primary
          ? 'bg-gradient-to-br from-primary-500/30 to-primary-600/20'
          : 'bg-surface-700'
        }
      `}>
        {icon}
      </div>

      {/* Title & Subtitle */}
      <h3 className={`text-xl font-semibold text-center ${primary ? 'text-primary-300' : 'text-white'}`}>
        {title}
      </h3>
      <p className="text-sm text-text-tertiary text-center mb-4">{subtitle}</p>

      {/* Items List */}
      <ul className="space-y-2 flex-1 mb-6">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-text-secondary flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${primary ? 'bg-primary-500' : 'bg-surface-500'}`} />
            {item}
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <div className={`
        w-full py-2.5 rounded-lg text-sm font-medium text-center transition-colors
        ${primary
          ? 'bg-primary-500 hover:bg-primary-400 text-white'
          : 'bg-surface-700 hover:bg-surface-600 text-text-primary'
        }
      `}>
        {cta} →
      </div>
    </Link>
  );
}

// ============================================================================
// RECENT ACTIVITY BAR - Shows last activity summary
// ============================================================================

function RecentActivityBar({
  clientId,
  language,
}: {
  clientId: string;
  language: string;
}) {
  const t = language === 'es';

  // TODO: Connect to actual activity data
  // For now, show placeholder

  return (
    <div className="mt-10 pt-6 border-t border-surface-700 w-full max-w-5xl">
      <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-text-tertiary">
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-surface-500" />
          {t ? 'Sin simulaciones recientes' : 'No recent simulations'}
        </span>
        <span className="hidden md:inline text-surface-600">|</span>
        <Link
          to="/library"
          className="text-primary-400 hover:text-primary-300 transition-colors"
        >
          {t ? 'Ver historial' : 'View history'} →
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// WELCOME STATE - No client selected
// ============================================================================

function WelcomeState({ language, isLoading }: { language: string; isLoading: boolean }) {
  const t = language === 'es';

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary-500/20 flex items-center justify-center text-3xl mx-auto mb-6">
          👆
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">
          {t ? 'Selecciona un Cliente' : 'Select a Client'}
        </h1>
        <p className="text-text-secondary mb-6">
          {t
            ? 'Usa el menú en la esquina superior derecha para seleccionar un cliente.'
            : 'Use the dropdown in the top-right corner to select a client.'}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(value: number | undefined, currency: string): string {
  if (!value) return '—';
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B ${currency}`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M ${currency}`;
  return `$${value.toLocaleString()} ${currency}`;
}

function getDataTier(marketData: any): { icon: string; label: string } {
  if (!marketData) return { icon: '⚪', label: 'None' };

  const hasMarketSize = !!marketData?.market?.total_value;
  const hasShares = !!marketData?.top_brands?.length;
  const hasGrowth = marketData?.market?.growth_rate !== undefined;

  const score = [hasMarketSize, hasShares, hasGrowth].filter(Boolean).length;

  if (score >= 3) return { icon: '🥇', label: 'Gold' };
  if (score >= 2) return { icon: '🥈', label: 'Silver' };
  if (score >= 1) return { icon: '🥉', label: 'Bronze' };
  return { icon: '⚪', label: 'None' };
}
